import { useMemo, useState } from "react";
import clsx from "clsx";

import { SummaryCollapse } from "./SummaryCollapse";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { SelectionFeature, SelectionType } from "src/js/stores/map/store";
import { Streamdown } from "streamdown";
import { Button } from "react-migration/components/Button";
import { UIMessage } from "@ai-sdk/react";

export const StreamingSummary = ({
  content,
  selection,
  setDetailSelection,
  generating,
  onOpenInEditor,
}: {
  content: UIMessage[];
  selection: SelectionFeature;
  setDetailSelection: (selection: SelectionFeature) => void;
  regenerate?: () => void;
  generating: boolean;
  onOpenInEditor?: (text: string) => void;
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  const latestMessagePart = content
    .filter((x) => x.role === "assistant")?.[0]
    ?.parts.findLast((part) => part.type === "text");

  const latestMessageText = useMemo(() => {
    return latestMessagePart?.type === "text" ? latestMessagePart.text : null;
  }, [latestMessagePart]);

  const isSiteSelection = selection.type === SelectionType.SITE;

  const canOpenInEditor = useMemo(() => {
    return onOpenInEditor && isSiteSelection && latestMessageText && !generating;
  }, [onOpenInEditor, isSiteSelection, latestMessageText, generating]);

  return (
    <>
      <div
        data-testid="summary-wrapper"
        className={clsx(
          "flex flex-col w-full transition-all ease-in-out duration-500",
          isExpanded ? "max-h-[2000px] overflow-y-auto" : "max-h-32 pb-8 overflow-hidden"
        )}
      >
        {/* Leverages the href data-href from the summary line to open the planning application details */}
        <div data-testid="summary-content-wrapper" className="px-1">
          <Streamdown
            components={{
              "planning-app": ({ id, children }) => {
                return (
                  <a
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      setDetailSelection({
                        id,
                        type: SelectionType.PLANNING_APPLICATION,
                      });
                    }}
                  >
                    {children}
                  </a>
                );
              },
            }}
          >
            {latestMessagePart?.type === "text" ? latestMessagePart.text : null}
          </Streamdown>
        </div>
        {canOpenInEditor && (
          <Button
            variant="tertiary"
            disabled={generating}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            onClick={() => onOpenInEditor!(latestMessageText!)}
            leadIcon="lt-edit-line"
          >
            {t("planning.ai.open_in_editor")}
          </Button>
        )}
      </div>
      <SummaryCollapse isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
    </>
  );
};
